import { trackEvent } from "@leafly-com/web-utils";
import { useSelector } from "react-redux";

import { MenuItem } from "custom-types/MenuItem";
import { VariantWithDeliveryInfo } from "custom-types/Variant";
import { getCurrentDispensary } from "redux/selectors/dispensary";

import MenuItemCard from "components/botanic/MenuItemCard";
import DispensaryMenuItemCard from "components/DispensaryMenuItemCard/DispensaryMenuItemCard";
import { MenuItemCardActions } from "components/Shared/MenuItemCarousel/MenuItemCardActions";

type Props = {
  menuItem: MenuItem;
  onAddToBagClick?: () => void;
  onClick?: () => void;
  oneCardPerRow?: boolean;
  onReservation?: () => void;
};

export const getMenuItemIndicator = (
  menuItem: MenuItem | VariantWithDeliveryInfo
) => {
  if (!menuItem) {
    return;
  }

  const { isTrending, isBestSeller, isNew, isStaffPick } = menuItem;

  let cardFlagText: string | undefined;
  let showStaffPick: boolean | undefined;
  if (isTrending) {
    cardFlagText = "Trending now";
  } else if (isBestSeller) {
    cardFlagText = "Best seller";
  } else if (isNew) {
    cardFlagText = "New";
  } else if (isStaffPick) {
    showStaffPick = true;
    cardFlagText = "Staff pick";
  }

  return { cardFlagText, showStaffPick };
};

const MenuCard: React.FC<Props> = ({
  menuItem,
  onAddToBagClick = () => {},
  onClick = () => {},
  oneCardPerRow,
  onReservation = () => {},
}) => {
  const { id: dispensaryId, acceptsOrders } = useSelector(getCurrentDispensary);
  const { brand, deal, menuItemId, pickupEnabled } = menuItem;
  const menuCardIndicators = getMenuItemIndicator(menuItem);
  const { id: brandId } = brand || {};
  const { id: dealId } = deal || {};

  const handleCardClick = () => {
    onClick();
    trackEvent(
      "Dispensary Menu",
      "click",
      `Detail View - ${
        pickupEnabled && acceptsOrders ? "Reservable" : "Not Reservable"
      }`,
      { brandId, dealId, dispensaryId, menuItemId }
    );

    if (menuCardIndicators?.cardFlagText) {
      trackEvent(
        "menu_card_indicator",
        "click",
        menuCardIndicators?.cardFlagText
      );
    }
    if (menuItem?.offers?.length) {
      const { dispensary, offers } = menuItem;
      const label = `${dispensary.name} - ${dispensary.id} (#) - ${
        offers?.length > 1
          ? "Multiple Offers"
          : `${offers[0].title} - ${offers[0].id}`
      }`;
      trackEvent("Dispensary Menu", "Click", label);
    }
  };

  return oneCardPerRow ? (
    <DispensaryMenuItemCard
      menuItem={menuItem}
      menuCardIndicators={menuCardIndicators}
      handleCardClick={handleCardClick}
      useNextLink={false}
      onAddToBagClick={onAddToBagClick}
      onReservation={onReservation}
    />
  ) : (
    <MenuItemCard
      useNextLink={false}
      handleCardClick={handleCardClick}
      menuItem={menuItem}
      menuCardIndicators={menuCardIndicators}
    >
      <MenuItemCardActions
        menuItem={menuItem}
        onClick={onAddToBagClick}
        onReservation={onReservation}
      />
    </MenuItemCard>
  );
};

export default MenuCard;
